'use client';

import type { Vehicle } from '@/utils/types';
import type { PrecioWebModo } from '@/lib/precio-web';
import { useTranslation } from '@/i18n/hooks/useTranslation';

/**
 * El bloque de precio de la ficha (`VehicleDetailSection`), en su propio archivo
 * para que el harness `test:precio-web` lo monte sin arrastrar la ficha entera
 * (supabase, agenda, modales).
 *
 * Con `modo = 'descuento'` el markup es EXACTAMENTE el de siempre: precio con
 * descuento grande y precio de lista tachado; sin descuento, un solo precio.
 * Con `'contado_financiamiento'` los dos números llevan rótulo y nada se tacha
 * (ticket #171; la regla está en `lib/precio-web`).
 */
export function PriceBlock({ v, formattedPrice, discountedPrice, modo = 'descuento' }: {
  v: Vehicle, formattedPrice: string, discountedPrice: string | null, modo?: PrecioWebModo
}) {
  const { t } = useTranslation();
  // Textos fijos (catalog.*), por el i18n del sitio; si la clave no resuelve,
  // el español de siempre.
  const tx = (key: string, fallback: string) => {
    const val = t?.(key) ?? '';
    return !val || val === key ? fallback : val;
  };
  if (v.discount_percentage && modo === 'contado_financiamiento') {
    return (
      <div className="space-y-3" data-precio-modo="contado_financiamiento">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {tx('catalog.card.financingPrice', 'Precio financiamiento')}
          </p>
          <p className="text-[36px] md:text-[42px] font-black text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-800 dark:from-white dark:to-white/90 leading-none tracking-tight tabular-nums">
            {discountedPrice}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {tx('catalog.card.cashPrice', 'Precio contado')}
          </p>
          <p className="text-xl md:text-2xl font-bold text-gray-700 dark:text-gray-200 leading-none tracking-tight tabular-nums">
            {formattedPrice}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {v.discount_percentage ? (
        <>
          <p className="text-[36px] md:text-[42px] font-black text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-800 dark:from-white dark:to-white/90 leading-none tracking-tight tabular-nums">
            {discountedPrice}
          </p>
          <p className="text-sm line-through text-gray-400 dark:text-gray-500 tabular-nums">
            {formattedPrice}
          </p>
        </>
      ) : (
        <p className="text-[36px] md:text-[42px] font-black text-gray-900 dark:text-white leading-none tracking-tight tabular-nums">
          {formattedPrice}
        </p>
      )}
    </div>
  );
}

export default PriceBlock;
